import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Video, Plus, ArrowLeft, Phone, Calendar, Clock, User, Link2, Star, PlayCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO = '/kardif-logo.png';

const STATUS_MAP = {
  scheduled: { label: 'Programmée', color: 'bg-blue-100 text-blue-800' },
  in_progress: { label: 'En cours', color: 'bg-green-100 text-green-800' },
  completed: { label: 'Terminée', color: 'bg-gray-100 text-gray-700' },
};

function VisioHub() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canCreate = user && (user.role === 'admin' || user.role === 'secretary');

  const [sessions, setSessions] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    client_name: '',
    client_phone: '',
    scheduled_date: new Date().toISOString().slice(0, 10),
    scheduled_time: '',
    technician_id: '',
    notes: '',
    intervention_id: '',
    intervention_label: '',
    donneur_ordre_nom: '',
    numero_sinistre: '',
    nom_assurance: '',
    client_email: '',
    documents: [],
  });
  const [uploadingDoc, setUploadingDoc] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const reqs = [axios.get(`${API}/visio/sessions`)];
      if (canCreate) {
        reqs.push(axios.get(`${API}/users/technicians`));
        reqs.push(axios.get(`${API}/interventions`).catch(() => ({ data: [] })));
      }
      const res = await Promise.all(reqs);
      setSessions(res[0].data || []);
      if (canCreate) {
        setTechnicians(res[1]?.data || []);
        setInterventions(res[2]?.data || []);
      }
    } catch (e) {
      toast.error('Erreur lors du chargement des sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.client_name || !form.client_phone || !form.scheduled_date) {
      toast.error('Nom, téléphone et date du client sont requis');
      return;
    }
    setCreating(true);
    try {
      const payload = { ...form };
      if (!payload.technician_id) delete payload.technician_id;
      if (!payload.intervention_id) { delete payload.intervention_id; delete payload.intervention_label; }
      await axios.post(`${API}/visio/sessions`, payload);
      toast.success('Session Visio créée');
      setShowDialog(false);
      setForm({
        client_name: '', client_phone: '',
        scheduled_date: new Date().toISOString().slice(0, 10),
        scheduled_time: '', technician_id: '', notes: '',
        intervention_id: '', intervention_label: '',
        donneur_ordre_nom: '', numero_sinistre: '', nom_assurance: '',
        client_email: '', documents: [],
      });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de la création');
    } finally {
      setCreating(false);
    }
  };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Veuillez choisir un fichier PDF'); return; }
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const up = await axios.post(`${API}/upload`, fd);
      setForm((f) => ({ ...f, documents: [...f.documents, { name: file.name, url: up.data.url }] }));
      toast.success('Document ajouté');
    } catch (err) {
      toast.error("Échec de l'ajout du document");
    } finally {
      setUploadingDoc(false);
      e.target.value = '';
    }
  };

  const copyClientLink = (token) => {    const link = `${window.location.origin}/visio/${token}`;
    navigator.clipboard.writeText(link).then(
      () => toast.success('Lien client copié ! Envoyez-le par SMS ou email.'),
      () => toast.error('Impossible de copier le lien')
    );
  };

  const backRoute = user?.role === 'admin' ? '/admin' : user?.role === 'secretary' ? '/secretary' : '/dashboard';

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="visio-hub-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => nav(backRoute)} data-testid="visio-back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <img src={LOGO} alt="KARDIF" className="h-8 w-auto" />
            <div className="flex items-center gap-2">
              <Video className="h-5 w-5 text-[#8B7355]" />
              <h1 className="text-lg font-semibold text-[#2D2D2D]">Visio-Assistance</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canCreate && (
              <Button variant="outline" onClick={() => nav('/visio-ratings')} data-testid="visio-ratings-link" className="hidden sm:flex">
                <Star className="h-4 w-4 mr-2" /> Évaluations
              </Button>
            )}
            {canCreate && (
              <Button onClick={() => setShowDialog(true)} className="bg-[#8B7355] hover:bg-[#7A6548]" data-testid="visio-new-session-btn">
                <Plus className="h-4 w-4 mr-2" /> Nouvelle session
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-[#8B7355]" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-20" data-testid="visio-empty-state">
            <Video className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-[#6B7280] mb-4">Aucune session Visio pour le moment.</p>
            {canCreate && (
              <Button onClick={() => setShowDialog(true)} className="bg-[#8B7355] hover:bg-[#7A6548]">
                <Plus className="h-4 w-4 mr-2" /> Créer une session
              </Button>
            )}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2" data-testid="visio-sessions-list">
            {sessions.map((s) => {
              const st = STATUS_MAP[s.status] || STATUS_MAP.scheduled;
              return (
                <Card key={s.id} className="overflow-hidden" data-testid={`visio-session-card-${s.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-[#8B7355]" />
                        <span className="font-semibold text-[#2D2D2D]">{s.client_name}</span>
                      </div>
                      <Badge className={st.color}>{st.label}</Badge>
                    </div>
                    <div className="space-y-1 text-sm text-[#6B7280] mb-4">
                      <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {s.client_phone}</div>
                      <div className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5" /> {s.scheduled_date}{s.scheduled_time ? ` à ${s.scheduled_time}` : ''}</div>
                      {s.technician_name && <div className="flex items-center gap-2"><User className="h-3.5 w-3.5" /> {s.technician_name}</div>}
                      {typeof s.client_rating === 'number' && (
                        <div className="flex items-center gap-1 text-yellow-600"><Star className="h-3.5 w-3.5 fill-yellow-500 text-yellow-500" /> {s.client_rating}/5</div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        className="bg-[#8B7355] hover:bg-[#7A6548]"
                        onClick={() => nav(`/visio/session/${s.id}`)}
                        data-testid={`visio-start-btn-${s.id}`}
                      >
                        <PlayCircle className="h-4 w-4 mr-1" />
                        {s.status === 'in_progress' ? "Rejoindre" : "Démarrer l'appel"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyClientLink(s.token)}
                        data-testid={`visio-copy-link-btn-${s.id}`}
                      >
                        <Link2 className="h-4 w-4 mr-1" /> Lien client
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Create dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md" data-testid="visio-create-dialog">
          <DialogHeader>
            <DialogTitle>Nouvelle session Visio</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nom du client *</Label>
              <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="visio-form-client-name" />
            </div>
            <div>
              <Label>Téléphone du client *</Label>
              <Input value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} data-testid="visio-form-client-phone" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} data-testid="visio-form-date" />
              </div>
              <div>
                <Label>Heure</Label>
                <Input type="time" value={form.scheduled_time} onChange={(e) => setForm({ ...form, scheduled_time: e.target.value })} data-testid="visio-form-time" />
              </div>
            </div>
            <div>
              <Label>Technicien</Label>
              <Select value={form.technician_id} onValueChange={(v) => setForm({ ...form, technician_id: v })}>
                <SelectTrigger data-testid="visio-form-tech"><SelectValue placeholder="Moi-même par défaut" /></SelectTrigger>
                <SelectContent>
                  {technicians.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Dossier d'intervention (lier au dossier)</Label>
              <Select value={form.intervention_id} onValueChange={(v) => {
                const it = interventions.find((x) => x.id === v);
                setForm({ ...form, intervention_id: v, intervention_label: it ? `${it.numero_dossier || 'Sans n°'} · ${it.nom_sinistre || it.client_nom || ''}`.trim() : '' });
              }}>
                <SelectTrigger data-testid="visio-form-intervention"><SelectValue placeholder="Aucun (optionnel)" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {interventions.map((it) => (
                    <SelectItem key={it.id} value={it.id}>
                      {(it.numero_dossier || 'Sans n°')} · {(it.nom_sinistre || it.client_nom || '').trim()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#6B7280] mt-1">Les photos et le rapport de la visio pourront être enregistrés dans ce dossier.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Donneur d'ordre</Label>
                <Input value={form.donneur_ordre_nom} onChange={(e) => setForm({ ...form, donneur_ordre_nom: e.target.value })} placeholder="Ex : DOMUS" data-testid="visio-form-donneur" />
              </div>
              <div>
                <Label>N° de sinistre</Label>
                <Input value={form.numero_sinistre} onChange={(e) => setForm({ ...form, numero_sinistre: e.target.value })} data-testid="visio-form-sinistre" />
              </div>
            </div>
            <div>
              <Label>Assurance</Label>
              <Input value={form.nom_assurance} onChange={(e) => setForm({ ...form, nom_assurance: e.target.value })} data-testid="visio-form-assurance" />
            </div>
            <div>
              <Label>Email du client (pour envoi du document signé)</Label>
              <Input type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} placeholder="client@email.com" data-testid="visio-form-client-email" />
            </div>
            <div>
              <Label>Documents à faire signer (PDF)</Label>
              <div className="space-y-1 mt-1">
                {form.documents.map((d, i) => (
                  <div key={i} className="flex items-center justify-between bg-gray-100 rounded px-2 py-1 text-xs" data-testid={`visio-form-doc-${i}`}>
                    <span className="truncate">{d.name}</span>
                    <button type="button" onClick={() => setForm({ ...form, documents: form.documents.filter((_, j) => j !== i) })} className="text-red-500 ml-2">✕</button>
                  </div>
                ))}
                <label className="flex items-center gap-2 text-sm text-[#8B7355] cursor-pointer">
                  <input type="file" accept="application/pdf" className="hidden" onChange={uploadDoc} data-testid="visio-form-doc-input" />
                  <span className="inline-flex items-center gap-1 border border-dashed border-[#8B7355] rounded px-3 py-1.5">
                    {uploadingDoc ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Ajouter un PDF
                  </span>
                </label>
              </div>
              <p className="text-xs text-[#6B7280] mt-1">Le technicien pourra les pousser à l'écran du client pendant l'appel pour signature.</p>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="visio-form-notes" />
            </div>
            <Button onClick={handleCreate} disabled={creating} className="w-full bg-[#8B7355] hover:bg-[#7A6548]" data-testid="visio-form-submit">
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Créer la session
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default VisioHub;
